import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  CardActionArea,
  Radio,
  Card,
  RadioGroup,
  Typography,
  FormControl,
  FormLabel,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  TextField,
  FormHelperText,
} from "@mui/material";

import { days } from "../../../../../../enums/days.enum";
import { months } from "../../../../../../enums/months.enum";
import TimeField from "../../../../../../shared/Form/TimeField";
import MultiChipCheck from "../../../../../../shared/Form/MultiChipCheck";
import { useCountryListing } from "../../../../../../api/queries/network/useCountryListing";

import { CreateServiceFormData } from "../../../../types/service.type";
import { formatDateIntoAmPm } from "../../../../../../utils/formatDate";
import { ServiceAvailabilityEnum } from "../../../../enum/service.enum";

const AvailabilityAndDelivery = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.availabilityAndDeliveryStep" });
  const { t: tDays } = useTranslation("common", { keyPrefix: "day" });
  const { t: tMonths } = useTranslation("common", { keyPrefix: "month" });
  const serviceDeliveryOptions = [
    {
      value: "online",
      label: t("online"),
      description: t("itWillBeOnlineService"),
    },
    {
      value: "offline",
      label: t("offline"),
      description: t("needToMeetRequestedServiceInLocation"),
    },
  ];

  const { countries, isCountriesDataLoading } = useCountryListing();

  const countriesList =
    countries.map(({ label, value }: { label: string; value: string }) => ({
      label,
      value,
    })) ?? [];

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<CreateServiceFormData>();

  const isAlwaysAvailable = watch("availability.alwaysAvailable");
  const isAvailableAllDay = watch("availability.availableAllDay");

  const isOnline = watch("availability.option") === ServiceAvailabilityEnum.Online;
  return (
    <Box sx={styles.container}>
      <Controller
        name="availability.option"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth component="fieldset" error={!!errors?.availability?.option}>
            <FormLabel sx={styles.formLabel}>{t("serviceDelivery")}</FormLabel>
            <RadioGroup sx={styles.serviceDeliveryRadioGroup}>
              {serviceDeliveryOptions.map(({ value, label, description }) => {
                return (
                  <Card key={value} sx={styles.serviceDeliveryCard(false)} onClick={() => field.onChange(value)}>
                    <Radio size="small" value={value} checked={field.value === value} />
                    <CardActionArea sx={styles.serviceDeliveryCardActionArea} disableRipple>
                      <Typography sx={styles.serviceDeliveryCardTitle}>{label}</Typography>
                      <Typography sx={styles.serviceDeliveryCardDescription}>{description}</Typography>
                    </CardActionArea>
                  </Card>
                );
              })}
            </RadioGroup>
            {errors?.availability?.option && (
              <FormHelperText error>{t(errors?.availability?.option.message as string)}</FormHelperText>
            )}
          </FormControl>
        )}
      />
      <Divider />
      <Controller
        name="availability.alwaysAvailable"
        control={control}
        render={({ field }) => (
          <FormControl sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box>
              <Typography sx={styles.formLabel}>{t("yourAvailability")}</Typography>
              <Typography sx={styles.formLabelDescription}>{t("thePreferredServiceDateTiming")}</Typography>
            </Box>
            <FormControlLabel
              sx={styles.formControlLabel}
              control={
                <Switch
                  size="small"
                  checked={field.value}
                  onChange={(event, checked) => {
                    field.onChange(checked);
                    if (checked) {
                      setValue("availability.availableAllDay", true);
                    } else {
                      setValue("availability.availableAllDay", false);
                    }
                  }}
                />
              }
              label={t("alwaysAvailable")}
            />
          </FormControl>
        )}
      />
      {!isAlwaysAvailable && (
        <>
          <Divider />
          <Typography sx={styles.formLabel}>{t("setYourAvailability")}</Typography>
          <Controller
            name="availability.months"
            control={control}
            render={({ field }) => (
              <FormControl sx={styles.formControl} error={!!errors?.availability?.months}>
                <FormLabel sx={styles.formChildLabel} error={!!errors?.availability?.months}>
                  {t("mostAvailableInMonths")}
                </FormLabel>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                  {months.map((month) => {
                    const isSelected = field.value?.includes(month);
                    return (
                      <MultiChipCheck
                        key={month}
                        label={tMonths(month)}
                        isSelected={isSelected ?? false}
                        handleOnClick={() => {
                          if (isSelected) {
                            field.onChange(field.value?.filter((d) => d !== month) ?? []);
                          } else {
                            field.onChange([...(field.value ?? []), month]);
                          }
                        }}
                      />
                    );
                  })}
                </Box>
                {errors?.availability?.months && (
                  <FormHelperText error>{t(errors?.availability?.months.message as string)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Controller
            name="availability.days"
            control={control}
            render={({ field }) => (
              <FormControl sx={styles.formControl} error={!!errors?.availability?.days}>
                <FormLabel sx={styles.formChildLabel} error={!!errors?.availability?.days}>
                  {t("mostAvailableDaysOfWeek")}
                </FormLabel>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                  {days.map((day) => {
                    const isSelected = field.value?.includes(day);
                    return (
                      <MultiChipCheck
                        key={day}
                        label={tDays(day)}
                        isSelected={isSelected ?? false}
                        handleOnClick={() => {
                          if (isSelected) {
                            field.onChange(field.value?.filter((d) => d !== day) ?? []);
                          } else {
                            field.onChange([...(field.value ?? []), day]);
                          }
                        }}
                      />
                    );
                  })}
                </Box>
                {errors?.availability?.days && (
                  <FormHelperText error>{t(errors.availability.days.message as string)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Controller
            name="availability.availableAllDay"
            control={control}
            render={({ field }) => (
              <FormControl sx={styles.formControl}>
                <Typography sx={styles.formChildLabel}>{t("mostAvailableTimeOfDay")}</Typography>
                <FormControlLabel
                  sx={styles.formControlLabel}
                  control={<Switch size="small" checked={field.value} onChange={field.onChange} />}
                  label={t("always")}
                />
              </FormControl>
            )}
          />
          {!isAvailableAllDay && (
            <>
              <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
                <Controller
                  name="availability.from"
                  control={control}
                  render={({ field }) => (
                    <FormControl sx={styles.formControl} error={!!errors?.availability?.from}>
                      <FormLabel sx={styles.formChildLabel} error={!!errors?.availability?.from}>
                        {t("fromHour")}
                      </FormLabel>
                      <TimeField
                        disabled={false}
                        value={field.value}
                        handleOnChange={(value: string) => {
                          const formattedTime = formatDateIntoAmPm(value) as string;
                          field.onChange(formattedTime);
                        }}
                        helperText=""
                        hasError={false}
                      />
                      {errors?.availability?.from && (
                        <FormHelperText error>{t(errors?.availability?.from.message as string)}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name="availability.to"
                  control={control}
                  render={({ field }) => (
                    <FormControl sx={styles.formControl} error={!!errors?.availability?.to}>
                      <FormLabel sx={styles.formChildLabel} error={!!errors?.availability?.to}>
                        {t("toHour")}
                      </FormLabel>
                      <TimeField
                        disabled={false}
                        value={field.value}
                        handleOnChange={(value: string) => {
                          const formattedTime = formatDateIntoAmPm(value) as string;
                          field.onChange(formattedTime);
                        }}
                        helperText=""
                        hasError={false}
                      />
                      {errors?.availability?.to && (
                        <FormHelperText error>{t(errors?.availability?.to.message as string)}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
            </>
          )}
        </>
      )}
      {!isOnline && (
        <Controller
          name="availability.locations"
          control={control}
          render={({ field }) => {
            const handleOnChange = (_: any, newValue: { label: string; value: number }[]) => {
              field.onChange(newValue.map((item) => item.value));
            };
            return (
              <FormControl sx={styles.formControl} error={!!errors?.availability?.locations}>
                <FormLabel sx={styles.formChildLabel} error={!!errors?.availability?.locations}>
                  {t("country")}
                </FormLabel>
                <Autocomplete
                  disablePortal
                  multiple={true}
                  disabled={isCountriesDataLoading}
                  options={countriesList}
                  value={countriesList.filter((country: { label: string; value: number }) =>
                    field.value?.includes(country.value)
                  )}
                  onChange={handleOnChange}
                  renderInput={(params) => (
                    <TextField {...params} placeholder={t("selectCountry")} error={!!errors?.availability?.locations} />
                  )}
                />
                {errors?.availability?.locations && (
                  <FormHelperText error>{t(errors?.availability?.locations.message as string)}</FormHelperText>
                )}
              </FormControl>
            );
          }}
        />
      )}
    </Box>
  );
};

export default AvailabilityAndDelivery;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  serviceDeliveryRadioGroup: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  serviceDeliveryCard: (isSelected: boolean) => ({
    flex: 1,
    border: isSelected ? "1px solid var(--Green---100, #008B07)" : "1px solid var(--Gray-200, #E4E7EC)",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  }),
  serviceDeliveryCardActionArea: {
    display: "flex",
    flexDirection: " column",
    alignItems: "flex-start",
    gap: "4px",
    "& .MuiCardActionArea-focusHighlight": {
      backgroundColor: "transparent !important",
    },
  },
  serviceDeliveryCardTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  serviceDeliveryCardDescription: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  formLabelDescription: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  formControlLabel: {
    margin: 0,
    gap: "12px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  formChildLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  formControl: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
};
